import type { Testimonial } from "@/lib/testimonials";
import "./groups-testimonial-card.css";

interface GroupsTestimonialCardProps {
  testimonial: Testimonial;
}

/**
 * Up to two initials from the signature ("John Doe" → "JD",
 * "Margaret" → "MA"). Falls back to "?" when the name is unusable.
 */
function getInitials(signature: string): string {
  const parts = signature.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Deterministic gradient per signature so the same person always gets the
 * same colours but neighbours look distinct. djb2-ish hash → hue.
 */
function getGradient(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 33 + seed.charCodeAt(i)) | 0;
  }
  const h1 = ((hash % 360) + 360) % 360;
  const h2 = (h1 + 38) % 360;
  return `linear-gradient(135deg, hsl(${h1} 72% 52%), hsl(${h2} 62% 38%))`;
}

export default function GroupsTestimonialCard({
  testimonial,
}: GroupsTestimonialCardProps) {
  const signature = testimonial.signature?.trim() || "Anonymous";
  const role = testimonial.titleMoreDescription?.trim() ?? "";
  const description = testimonial.description ?? "";

  const initials = getInitials(signature);
  const gradient = getGradient(signature);

  return (
    <div className="gtc-card">
      <div className="gtc-stars" aria-label="Rated 5 out of 5">
        <i className="fa-sharp fa-solid fa-star"></i>
        <i className="fa-sharp fa-solid fa-star"></i>
        <i className="fa-sharp fa-solid fa-star"></i>
        <i className="fa-sharp fa-solid fa-star"></i>
        <i className="fa-sharp fa-solid fa-star"></i>
      </div>

      {/* Full testimonial body — scrolls inside a 120px window with a light
          custom scrollbar (see groups-testimonial-card.css). HTML is trusted
          CMS content, same as the rest of the description fields. */}
      <div
        className="gtc-text"
        dangerouslySetInnerHTML={{ __html: description }}
      />

      <hr className="gtc-divider" />

      <div className="gtc-author">
        <div
          className="gtc-monogram"
          aria-hidden="true"
          style={{ background: gradient }}
        >
          {initials}
        </div>
        <div>
          <h3 className="gtc-name">{signature}</h3>
          <div className="gtc-meta">
            {role ? (
              role
            ) : (
              <>
                <i className="fa-sharp fa-solid fa-circle-check"></i>
                <span>Verified Traveler</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
